import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { ArrowUpRight } from "lucide-react";

interface CaseStudyCardProps {
  to: string;
  image?: string;
  /** Describes what the still shows. Omit only when `video` makes it decorative. */
  imageAlt?: string;
  video?: string;
  gradient?: string;
  visual?: React.ReactNode;
  client: string;
  type: string;
  taglineLead: string;
  taglineEm: string;
  desc: string;
  stack: string[];
  meta: string;
}

export function CaseStudyCard({
  to,
  image,
  imageAlt,
  video,
  gradient,
  visual,
  client,
  type,
  taglineLead,
  taglineEm,
  desc,
  stack,
  meta,
}: CaseStudyCardProps) {
  const { t } = useTranslation();
  return (
    <Link
      to={to}
      preload="intent"
      className="group flex flex-col overflow-hidden rounded-[22px] border border-line bg-surface transition-all duration-200 hover:-translate-y-0.5 hover:border-line-strong"
    >
      {/* Cover */}
      <div
        className="relative flex aspect-[5/3] items-end overflow-hidden bg-surface-2 p-7"
        style={!image && gradient ? { background: gradient } : undefined}
      >
        {image && (
          <img
            src={image}
            alt={video ? "" : (imageAlt ?? "")}
            aria-hidden={video ? "true" : undefined}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        {video && (
          <video
            aria-hidden="true"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster={image}
            src={video}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        {visual}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent"
        />
        <span
          className="serif-italic relative leading-none text-white/95"
          style={{ fontSize: "clamp(36px, 4.6vw, 56px)" }}
        >
          {client}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col p-7">
        <div className="mono text-[11px] tracking-[0.16em] text-ink-3 uppercase">{type}</div>
        <h3 className="mt-3 text-[clamp(22px,2.4vw,30px)] leading-tight font-medium tracking-[-0.025em] text-ink">
          {taglineLead} <span className="serif-italic text-accent">{taglineEm}</span>
        </h3>
        <p className="mt-3 text-[14px] leading-[1.6] text-ink-2">{desc}</p>
        <div className="mt-5 mb-6 flex flex-wrap gap-1.5">
          {stack.map((s) => (
            <span
              key={s}
              className="mono rounded-full border border-line bg-surface-2 px-2 py-1 text-[11px] tracking-tight text-ink-2"
            >
              {s}
            </span>
          ))}
        </div>
        <div className="mt-auto flex items-center justify-between gap-3 border-t border-line pt-5">
          <span className="mono text-[11px] tracking-[0.14em] text-ink-3 uppercase">{meta}</span>
          <span className="inline-flex items-center gap-1 text-[13px] font-medium text-ink transition-colors group-hover:text-accent">
            {t("work.card.readCaseStudy")}
            <ArrowUpRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2}
            />
          </span>
        </div>
      </div>
    </Link>
  );
}
